import { Admin } from '@models/Admin'
import { useAuth } from '@modules/auth/contexts/Auth'
import SBPhoneInput, {
  SBPhoneNumber,
} from '@modules/sb-antd-phone-input/SBPhoneInput'
import { dispatchSignals } from '@prelude.so/js-sdk/signals'
import {
  requestTwoFactorAuthentication,
  updatePhoneTwoFactorAuthentication,
  verifyTwoFactorAuthentication,
} from '@services/TwoFactorAuthenticationService'
import { formatPhoneNumberToE164FromPhoneInput } from '@utils/PhoneFormatter'
import { sanitizeReturnTo } from '@utils/safeReturnTo'
import { isE164Phone, requiredRule } from '@utils/rules'
import { Button, Flex, Form, Input, Tooltip, Typography } from 'antd'
import { useEffect, useState } from 'react'

const { Text } = Typography

const TwoFactorAuthenticationPage = () => {
  const { currentUser, getLoggedUserInfos } = useAuth<Admin, {}>()
  const [isLoading, setIsLoading] = useState(false)
  const [resendCodeLoading, setResendCodeLoading] = useState(false)

  const [dispatchId, setDispatchId] = useState<string | undefined>(undefined)
  const [isDispatchLoading, setIsDispatchLoading] = useState(true)

  useEffect(() => {
    dispatchSignals(import.meta.env.VITE_PRELUDE_SDK_KEY)
      .then((id) => setDispatchId(id))
      .catch((error) => console.error(error))
      .finally(() => setIsDispatchLoading(false))
  }, [])

  const onFormFinish = (values: { code: string }) => {
    setIsLoading(true)
    verifyTwoFactorAuthentication(values.code)
      .then(async (response) => {
        if (response.message === 'auth.2fa.verified') {
          // Establish the full session in localStorage so that LayoutLogged
          // lets the user through after the hard redirect below.
          // This is especially important when 2FA was triggered before
          // loggedIn() had a chance to run (pre-2FA session scenario).
          await getLoggedUserInfos().catch(() => {})
          const returnTo = sanitizeReturnTo(
            sessionStorage.getItem('2fa.returnTo')
          )
          sessionStorage.removeItem('2fa.returnTo')
          window.location.href = returnTo
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const onPhoneFormFinish = (value: { phone: SBPhoneNumber }) => {
    setIsLoading(true)
    const phone = formatPhoneNumberToE164FromPhoneInput(value.phone)
    updatePhoneTwoFactorAuthentication(phone, dispatchId).finally(() => {
      setIsLoading(false)
      window.location.reload()
    })
  }

  const handleResendCode = () => {
    setResendCodeLoading(true)
    requestTwoFactorAuthentication(dispatchId).then(() => {
      setTimeout(() => {
        setResendCodeLoading(false)
      }, 10000)
    })
  }

  useEffect(() => {
    if (isDispatchLoading) return

    setIsLoading(true)
    // If we know the user has no phone yet, show the phone setup form instead
    if (currentUser !== undefined && currentUser.phone === null) {
      setIsLoading(false)
      return
    }
    // Send the 2FA SMS. Works with both full sessions and pre-2FA sessions
    // (where currentUser is undefined because getLoggedUserInfos hasn't
    // succeeded yet — the backend uses the session cookie directly).
    requestTwoFactorAuthentication(dispatchId).finally(() => {
      setIsLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDispatchLoading])

  // When currentUser is undefined we are in a pre-2FA session: the login API
  // call succeeded but getLoggedUserInfos() returned 401, so the loggedIn
  // callback was never fired.  Show the OTP form directly — the backend
  // knows the phone from the session cookie.
  const hasPhone = currentUser === undefined || !!currentUser.phone

  return (
    <Flex vertical gap={10} align="center" justify="center">
      {hasPhone ? (
        <>
          <Text className="m-0">Enter the OTP sent to you by SMS</Text>
          <Form onFinish={onFormFinish} className="w-full">
            <Form.Item
              name="code"
              style={{ justifyItems: 'center' }}
              rules={[requiredRule()]}
            >
              <Input.OTP
                length={4}
                size="large"
                formatter={(str) => str.toUpperCase()}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 15 }}>
              <Button
                block
                size="large"
                className="w-full"
                type="primary"
                htmlType="submit"
                loading={isLoading}
              >
                Next
              </Button>
            </Form.Item>
          </Form>
          <Tooltip
            placement="bottom"
            title="You can request a new code every 10 seconds !"
          >
            <Button
              size="small"
              className="w-full"
              type="dashed"
              loading={resendCodeLoading}
              onClick={handleResendCode}
              disabled={isDispatchLoading}
            >
              Resend code
            </Button>
          </Tooltip>
        </>
      ) : (
        <>
          <Text className="m-0">
            Please enter your phone number for two-factor authentication
          </Text>
          <Form onFinish={onPhoneFormFinish} className="w-full">
            <Form.Item name="phone" rules={[isE164Phone()]}>
              <SBPhoneInput disableParentheses enableSearch size="large" />
            </Form.Item>
            <Button
              block
              size="large"
              className="w-full"
              type="primary"
              htmlType="submit"
              loading={isLoading}
            >
              Submit
            </Button>
          </Form>
        </>
      )}
    </Flex>
  )
}

export default TwoFactorAuthenticationPage
